import React, { useState, useMemo, useEffect } from 'react';
import { KoreanTextModel, BookModel } from '../../../../models';
import {
    KOREAN_CATEGORIES,
    READING_BRANCHES,
    LITERATURE_BRANCHES,
    SPEECH_WRITING_BRANCHES,
    LANGUAGE_MEDIA_BRANCHES,
    getPageNumberFromId
} from '../../../../models';
import { db } from '../../../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../../../../models/collectionNames';
import { ArrowLeft, BookOpen, Save, FileText, CheckCircle } from 'lucide-react';
import ConceptExplain from './ConceptExplain';
import PracticeQuestions from './PracticeQuestions';

interface EditUnitRootProps {
    unit: KoreanTextModel;
    bookId: string;
    activeBook: BookModel;
    onBack: (updatedUnit?: KoreanTextModel) => void;
}

const EditUnitRoot: React.FC<EditUnitRootProps> = ({ unit, bookId, activeBook, onBack }) => {
    const [localUnit, setLocalUnit] = useState<KoreanTextModel>(unit);
    const [activeTab, setActiveTab] = useState<'concept' | 'questions'>('concept');
    const [isSavingMetadata, setIsSavingMetadata] = useState(false);

    // Metadata form states
    const [editTitle, setEditTitle] = useState(unit.title || '');
    const [editKoreanText, setEditKoreanText] = useState(unit.korean || '');
    const [editCategory, setEditCategory] = useState(unit.category || '');
    const [editBranch, setEditBranch] = useState(unit.branch || '');
    const [editGenre, setEditGenre] = useState(unit.genre || '');
    const [editThemes, setEditThemes] = useState(
        Array.isArray(unit.themes) ? unit.themes.join(', ') : ''
    );

    const isKorean = activeBook.subject === '국어';
    const pageNum = getPageNumberFromId(unit.id);

    // Sync state when parent unit object updates
    useEffect(() => {
        setLocalUnit(unit);
        setEditTitle(unit.title || '');
        setEditKoreanText(unit.korean || '');
        setEditCategory(unit.category || '');
        setEditBranch(unit.branch || '');
        setEditGenre(unit.genre || '');
        setEditThemes(Array.isArray(unit.themes) ? unit.themes.join(', ') : '');
    }, [unit]);

    const handleLocalUpdate = (updatedFields: Partial<KoreanTextModel>) => {
        setLocalUnit(prev => ({
            ...prev,
            ...updatedFields
        }));
    };

    // Category and Subcategory options mapping
    const branchOptions = useMemo(() => {
        if (editCategory === '독서') return Object.keys(READING_BRANCHES);
        if (editCategory === '문학') return Object.keys(LITERATURE_BRANCHES);
        if (editCategory === '화작') return Object.keys(SPEECH_WRITING_BRANCHES);
        if (editCategory === '언매') return Object.keys(LANGUAGE_MEDIA_BRANCHES);
        return [];
    }, [editCategory]);

    const genreOptions = useMemo(() => {
        if (!editCategory || !editBranch) return [];
        if (editCategory === '독서') return (READING_BRANCHES as any)[editBranch] || [];
        if (editCategory === '문학') return (LITERATURE_BRANCHES as any)[editBranch] || [];
        if (editCategory === '화작') return (SPEECH_WRITING_BRANCHES as any)[editBranch] || [];
        if (editCategory === '언매') return (LANGUAGE_MEDIA_BRANCHES as any)[editBranch] || [];
        return [];
    }, [editCategory, editBranch]);

    const handleCategoryChange = (cat: string) => {
        setEditCategory(cat as any);
        setEditBranch('');
        setEditGenre('');
    };

    const handleBranchChange = (br: string) => {
        setEditBranch(br as any);
        setEditGenre('');
    };

    const handleSaveMetadata = async () => {
        setIsSavingMetadata(true);
        const parsedThemes = editThemes
            .split(',')
            .map(t => t.trim())
            .filter(Boolean);

        const updatedData: Partial<KoreanTextModel> = {
            title: editTitle.trim(),
            korean: editKoreanText.trim(),
            updatedAt: Date.now()
        };

        if (isKorean) {
            updatedData.category = (editCategory || undefined) as any;
            updatedData.branch = (editBranch || undefined) as any;
            updatedData.genre = (editGenre || undefined) as any;
            updatedData.themes = parsedThemes;
        }

        try {
            const docRef = doc(db, COLLECTIONS.BOOK_UNIT, unit.id);
            await updateDoc(docRef, updatedData);
            
            handleLocalUpdate(updatedData);
            alert('페이지 기본 메타데이터 정보가 수정되었습니다.');
        } catch (err: any) {
            console.error('Failed to update metadata:', err);
            alert('정보 수정 실패: ' + err.message);
        } finally {
            setIsSavingMetadata(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 w-full overflow-hidden animate-in fade-in duration-200">
            {/* Top Navigation Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 shadow-sm">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => onBack(localUnit)}
                        className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 hover:text-slate-800 transition-all border border-slate-200 bg-white shadow-sm flex items-center gap-1.5 active:scale-95"
                        title="단원 목록으로 이동"
                    >
                        <ArrowLeft size={14} strokeWidth={3} />
                        <span className="text-xs font-black">목록으로</span>
                    </button>
                    
                    <div className="h-6 w-[1px] bg-slate-200 mx-1" />

                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-black text-slate-800">
                                Page {pageNum} 상세 정보 수정
                            </h2>
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[9px] font-black uppercase tracking-wider">
                                {activeBook.subject}
                            </span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold mt-0.5 truncate max-w-[300px]">
                            {localUnit.title || `Page ${pageNum}`}
                        </p>
                    </div>
                </div>

                {/* Status Badges */}
                <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-black ${
                        localUnit._stats?.conceptDone 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : 'bg-slate-50 text-slate-400 border-slate-200'
                    }`}>
                        <CheckCircle size={10} />
                        <span>개념설명 {localUnit._stats?.conceptDone ? '분석 완료' : '미완료'}</span>
                    </div>
                    <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-black ${
                        (localUnit._stats?.questionsCount || 0) > 0
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                            : 'bg-slate-50 text-slate-400 border-slate-200'
                    }`}>
                        <CheckCircle size={10} />
                        <span>실전문항 {localUnit._stats?.questionsCount || 0}문제</span>
                    </div>
                </div>
            </div>

            {/* Split Main Body Workspace */}
            <div className="flex-1 flex overflow-hidden bg-white">
                {/* Left Pane: Basic Meta Settings (1/3 Width) */}
                <div className="w-[360px] border-r border-slate-200 p-6 overflow-y-auto flex flex-col justify-between shrink-0 custom-scrollbar">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-1">
                            <BookOpen className="text-slate-400" size={15} />
                            <span className="text-xs font-black text-slate-800">페이지 기본 내용 설정</span>
                        </div>

                        {/* Page Title */}
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">학습 페이지 제목</label>
                            <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 transition-all"
                                placeholder={`Page ${pageNum} 제목`}
                            />
                        </div>

                        {/* Korean-only Category Selectors */}
                        {isKorean && (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">대분류</label>
                                        <select
                                            value={editCategory}
                                            onChange={(e) => handleCategoryChange(e.target.value)}
                                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 cursor-pointer"
                                        >
                                            <option value="">선택 안 함</option>
                                            {KOREAN_CATEGORIES.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">중분류</label>
                                        <select
                                            value={editBranch}
                                            onChange={(e) => handleBranchChange(e.target.value)}
                                            disabled={!editCategory}
                                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 cursor-pointer disabled:bg-slate-100 disabled:cursor-not-allowed"
                                        >
                                            <option value="">선택 안 함</option>
                                            {branchOptions.map(br => (
                                                <option key={br} value={br}>{br}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">소분류 (장르)</label>
                                        <select
                                            value={editGenre}
                                            onChange={(e) => setEditGenre(e.target.value)}
                                            disabled={!editBranch}
                                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 cursor-pointer disabled:bg-slate-100 disabled:cursor-not-allowed"
                                        >
                                            <option value="">선택 안 함</option>
                                            {genreOptions.map(gr => (
                                                <option key={gr} value={gr}>{gr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">핵심 테마 키워드</label>
                                        <input
                                            type="text"
                                            value={editThemes}
                                            onChange={(e) => setEditThemes(e.target.value)}
                                            placeholder="예: 성찰, 그리움, 소외감"
                                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Page original text */}
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">
                                {isKorean ? '지문 원문 텍스트' : '본문 텍스트 설명'}
                            </label>
                            <textarea
                                rows={10}
                                value={editKoreanText}
                                onChange={(e) => setEditKoreanText(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 custom-scrollbar"
                                placeholder={isKorean ? "국어 지문 내용을 입력해 주세요." : "교재 본문 및 공식을 입력해 주세요."}
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleSaveMetadata}
                        disabled={isSavingMetadata}
                        className="w-full mt-6 flex items-center justify-center gap-1.5 px-4 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black shadow-md transition-all active:scale-95 disabled:opacity-50"
                    >
                        <Save size={13} />
                        <span>{isSavingMetadata ? '저장 중...' : '정보 업데이트 완료'}</span>
                    </button>
                </div>

                {/* Right Pane: AI Concepts and Questions Tabs (2/3 Width) */}
                <div className="flex-1 p-6 overflow-hidden flex flex-col">
                    {/* Tab Navigation header */}
                    <div className="flex items-center gap-6 border-b border-slate-100 mb-5 shrink-0">
                        <button
                            onClick={() => setActiveTab('concept')}
                            className={`h-11 flex items-center gap-1.5 text-xs font-black relative transition-colors ${
                                activeTab === 'concept' ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            <FileText size={13} />
                            <span>개념설명 분석</span>
                            {activeTab === 'concept' && (
                                <div className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-indigo-600 rounded-full" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab('questions')}
                            className={`h-11 flex items-center gap-1.5 text-xs font-black relative transition-colors ${
                                activeTab === 'questions' ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            <BookOpen size={13} />
                            <span>실전문항 출제</span>
                            {activeTab === 'questions' && (
                                <div className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-indigo-600 rounded-full" />
                            )}
                        </button>
                    </div>

                    {/* Tab Content Component switcher */}
                    <div className="flex-1 overflow-hidden">
                        {activeTab === 'concept' ? (
                            <ConceptExplain
                                unit={localUnit}
                                bookId={bookId}
                                activeBook={activeBook}
                                onUpdate={handleLocalUpdate}
                                draftTitle={editTitle}
                                draftPassage={editKoreanText}
                            />
                        ) : (
                            <PracticeQuestions
                                unit={localUnit}
                                bookId={bookId}
                                activeBook={activeBook}
                                onUpdate={handleLocalUpdate}
                                draftPassage={editKoreanText}
                                draftGenre={editGenre}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditUnitRoot;
